import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const FILTROS_INICIAIS = { banco: 'vendizapfmp', situacao: '', tipo: '', busca: '', dias: '7' };

const SITUACAO_BADGE = {
  ok: { classe: 'bg-success', rotulo: 'Processado' },
  erro: { classe: 'bg-danger', rotulo: 'Erro' },
  ignorado: { classe: 'bg-secondary', rotulo: 'Ignorado' },
};

const TIPO_MOVIMENTO = {
  charge: 'Venda', payment: 'Venda (outros meios)', refund: 'Estorno', payment_refund: 'Estorno',
  payout: 'Repasse ao banco', payout_failure: 'Repasse devolvido', stripe_fee: 'Tarifa Stripe',
  adjustment: 'Ajuste / contestação', balance_transfer_inbound: 'Transferência interna (entrada)',
  balance_transfer_outbound: 'Transferência interna (saída)',
};

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataHora = (s) => {
  if (!s) return '—';
  const [d, h] = String(s).replace('T', ' ').slice(0, 19).split(' ');
  return `${d.split('-').reverse().join('/')}${h ? ` ${h.slice(0, 5)}` : ''}`;
};
const data = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—');

const StripeEventos = () => {
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [eventos, setEventos] = useState([]);
  const [resumo, setResumo] = useState({ ok: 0, erro: 0, ignorado: 0 });
  const [tipos, setTipos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [tabela, setTabela] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [carregando, setCarregando] = useState(true);
  const [movimentos, setMovimentos] = useState(null);
  const [carregandoMov, setCarregandoMov] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
  });

  const montarParams = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) p.append(k, v); });
    p.append('page', extra.page || page);
    p.append('page_size', pageSize);
    return p.toString();
  }, [filtros, page, pageSize]);

  const carregar = useCallback(async (paginaAlvo) => {
    setCarregando(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/stripe-eventos/?${montarParams({ page: paginaAlvo })}`,
        authHeader()
      );
      setEventos(resp.data.eventos || []);
      setResumo(resp.data.resumo || { ok: 0, erro: 0, ignorado: 0 });
      setTipos(resp.data.tipos || []);
      setBancos(resp.data.bancos || []);
      setTabela(resp.data.tabela || null);
      setTotal(resp.data.total || 0);
      if (resp.data.aviso) toast.info(resp.data.aviso);
    } catch (e) {
      toast.error('Erro ao carregar os eventos do webhook Stripe.');
    } finally {
      setCarregando(false);
    }
  }, [montarParams]);

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarFiltros = () => {
    setPage(1);
    carregar(1);
  };

  const limparFiltros = () => {
    setFiltros(FILTROS_INICIAIS);
    setPage(1);
    setTimeout(() => carregar(1), 0);
  };

  const mudarPagina = (nova) => {
    setPage(nova);
    carregar(nova);
  };

  const filtrarPorSituacao = (situacao) => {
    const novo = { ...filtros, situacao: filtros.situacao === situacao ? '' : situacao };
    setFiltros(novo);
    setPage(1);
    setTimeout(() => carregar(1), 0);
  };

  const abrirMovimentos = async (ev) => {
    if (!ev.objeto_id) return;
    setCarregandoMov(true);
    setMovimentos({ objeto: ev.objeto_id, tipo_evento: ev.tipo });
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/stripe-eventos/movimentos/?banco=${filtros.banco}&objeto=${encodeURIComponent(ev.objeto_id)}`,
        authHeader()
      );
      setMovimentos({ ...resp.data, tipo_evento: ev.tipo });
    } catch (e) {
      toast.error('Erro ao carregar os movimentos do objeto.');
      setMovimentos(null);
    } finally {
      setCarregandoMov(false);
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="container-fluid py-4">

      {/* Header padrão da plataforma */}
      <div className="card mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0" style={{ color: '#212529' }}>
            <i className="fa-brands fa-stripe-s me-2" style={{ color: '#d4af37' }}></i>
            Webhook Stripe — Eventos
          </h5>
          <small style={{ color: '#6c757d' }}>
            Tudo que o webhook recebeu do Stripe e o que foi gravado no extrato (tabela stripe)
          </small>
          <div style={{ width: 60, height: 3, background: '#06b6d4', marginTop: 8, borderRadius: 2 }}></div>
        </div>

        <div className="card-body">
          {/* Cards de resumo (clicáveis = filtro rápido) */}
          <div className="row g-2 mb-3">
            {[
              { chave: 'ok', titulo: 'Processados', cor: '#198754', icone: 'fa-circle-check' },
              { chave: 'erro', titulo: 'Com erro', cor: '#dc3545', icone: 'fa-circle-xmark' },
              { chave: 'ignorado', titulo: 'Ignorados', cor: '#6c757d', icone: 'fa-circle-minus' },
            ].map((c) => (
              <div className="col-md-4" key={c.chave}>
                <div
                  className="card h-100"
                  role="button"
                  onClick={() => filtrarPorSituacao(c.chave)}
                  style={{
                    cursor: 'pointer',
                    border: filtros.situacao === c.chave ? `2px solid ${c.cor}` : '1px solid #dee2e6',
                  }}
                >
                  <div className="card-body py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <small style={{ color: '#6c757d' }}>{c.titulo} (período)</small>
                      <h4 className="mb-0" style={{ color: c.cor }}>{resumo[c.chave] || 0}</h4>
                    </div>
                    <i className={`fas ${c.icone} fa-2x`} style={{ color: c.cor, opacity: 0.35 }}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Situação do extrato gravado */}
          {tabela && (
            <div className="row g-2 mb-3">
              {[
                { rotulo: 'Saldo no Stripe (pela tabela)', valor: brl(tabela.saldo_total), dica: 'Soma de todo o líquido gravado — tem que bater com o saldo do painel do Stripe' },
                { rotulo: 'A liberar (pendente)', valor: brl(tabela.saldo_pendente) },
                { rotulo: 'Movimentos gravados', valor: Number(tabela.movimentos).toLocaleString('pt-BR') },
                { rotulo: 'Último movimento', valor: dataHora(tabela.ultimo_movimento) },
                { rotulo: 'Último evento recebido', valor: dataHora(tabela.ultimo_evento) },
              ].map((c) => (
                <div className="col" key={c.rotulo}>
                  <div className="border rounded p-2 h-100" title={c.dica || ''}>
                    <small className="text-muted d-block">{c.rotulo}</small>
                    <strong style={{ fontSize: 15 }}>{c.valor}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div className="row g-2">
            <div className="col-md-2">
              <select className="form-select" value={filtros.banco}
                onChange={(e) => setFiltros((p) => ({ ...p, banco: e.target.value }))}>
                {(bancos.length ? bancos : [{ banco: 'vendizapfmp', nome: 'Vendizap' }]).map((b) => (
                  <option key={b.banco} value={b.banco}>{b.nome}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={filtros.dias}
                onChange={(e) => setFiltros((p) => ({ ...p, dias: e.target.value }))}>
                <option value="1">Últimas 24h</option>
                <option value="7">Últimos 7 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="90">Últimos 90 dias</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={filtros.situacao}
                onChange={(e) => setFiltros((p) => ({ ...p, situacao: e.target.value }))}>
                <option value="">Todas situações</option>
                <option value="ok">Processado</option>
                <option value="erro">Erro</option>
                <option value="ignorado">Ignorado</option>
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filtros.tipo}
                onChange={(e) => setFiltros((p) => ({ ...p, tipo: e.target.value }))}>
                <option value="">Todos os eventos</option>
                {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <input type="text" className="form-control" placeholder="ID do evento / objeto / detalhe"
                value={filtros.busca}
                onChange={(e) => setFiltros((p) => ({ ...p, busca: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && aplicarFiltros()} />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-dark" onClick={aplicarFiltros}>Filtrar</button>
            <button className="btn btn-outline-secondary" onClick={limparFiltros}>Limpar</button>
            <button className="btn btn-outline-dark ms-auto" onClick={() => carregar(page)}>
              <i className="fas fa-rotate me-2"></i>Atualizar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card">
        <div className="card-body">
          {carregando ? (
            <p style={{ color: '#6c757d' }}><i className="fas fa-spinner fa-spin me-2"></i>Carregando...</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Recebido em</th>
                      <th>Evento</th>
                      <th>Objeto</th>
                      <th>Situação</th>
                      <th className="text-end">Movimentos</th>
                      <th>Detalhe</th>
                      <th className="text-end">Tentativas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventos.map((ev) => {
                      const badge = SITUACAO_BADGE[ev.situacao] || { classe: 'bg-secondary', rotulo: ev.situacao };
                      return (
                        <tr key={ev.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{dataHora(ev.recebido_em)}</td>
                          <td><small>{ev.tipo}</small></td>
                          <td>
                            {ev.objeto_id ? (
                              <button
                                className="btn btn-link btn-sm p-0"
                                style={{ color: '#00205b', textDecoration: 'underline', fontWeight: 600 }}
                                title="Ver os movimentos gravados deste objeto"
                                onClick={() => abrirMovimentos(ev)}
                              >
                                {ev.objeto_id}
                              </button>
                            ) : '—'}
                          </td>
                          <td><span className={`badge ${badge.classe}`}>{badge.rotulo}</span></td>
                          <td className="text-end">{ev.movimentos}</td>
                          <td><small className="text-muted">{ev.detalhe || '—'}</small></td>
                          <td className="text-end"><small>{ev.tentativas}</small></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {eventos.length === 0 && (
                <p className="text-center text-muted my-4">
                  Nenhum evento no período. Assim que o Stripe enviar eventos, eles aparecem aqui.
                </p>
              )}

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small style={{ color: '#6c757d' }}>
                  {total} evento(s) — página {page} de {totalPaginas}
                </small>
                <div className="btn-group">
                  <button className="btn btn-sm btn-outline-dark" disabled={page <= 1}
                    onClick={() => mudarPagina(page - 1)}>Anterior</button>
                  <button className="btn btn-sm btn-outline-dark" disabled={page >= totalPaginas}
                    onClick={() => mudarPagina(page + 1)}>Próxima</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de movimentos do objeto */}
      {movimentos && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1070 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-white">
                  <div>
                    <h5 className="modal-title" style={{ color: '#212529' }}>
                      Movimentos de {movimentos.objeto}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      Evento {movimentos.tipo_evento} · o que está gravado na tabela stripe
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setMovimentos(null)}></button>
                </div>
                <div className="modal-body">
                  {carregandoMov ? (
                    <p style={{ color: '#6c757d' }}><i className="fas fa-spinner fa-spin me-2"></i>Carregando movimentos...</p>
                  ) : (movimentos.movimentos || []).length === 0 ? (
                    <div className="alert alert-light border py-2">
                      <small>Nenhum movimento gravado para este objeto (ex.: cobrança recusada não gera movimento de saldo).</small>
                    </div>
                  ) : (
                    <>
                      <div className="row g-2 mb-3">
                        {[
                          { rotulo: 'Bruto', valor: movimentos.totais.valor_bruto },
                          { rotulo: 'Tarifa', valor: movimentos.totais.tarifa },
                          { rotulo: 'Líquido', valor: movimentos.totais.valor_liquido },
                        ].map((c) => (
                          <div className="col-md-4" key={c.rotulo}>
                            <div className="border rounded p-2 h-100">
                              <small className="text-muted d-block">{c.rotulo}</small>
                              <strong style={{ fontSize: 14 }}>{brl(c.valor)}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                      {movimentos.objeto.startsWith('po_') && (
                        <div className="alert alert-light border py-2">
                          <small>
                            Num repasse, o líquido total deve dar R$ 0,00: as vendas que entraram nele
                            somam exatamente o valor que saiu para o banco.
                          </small>
                        </div>
                      )}
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Data</th>
                              <th>Tipo</th>
                              <th>Meio</th>
                              <th className="text-end">Bruto</th>
                              <th className="text-end">Tarifa</th>
                              <th className="text-end">Líquido</th>
                              <th>Situação</th>
                              <th>Disponível em</th>
                              <th>Repasse</th>
                            </tr>
                          </thead>
                          <tbody>
                            {movimentos.movimentos.map((m) => (
                              <tr key={m.id}>
                                <td style={{ whiteSpace: 'nowrap' }}><small>{dataHora(m.criado_em)}</small></td>
                                <td><small>{TIPO_MOVIMENTO[m.tipo] || m.tipo}</small></td>
                                <td>
                                  <small>
                                    {[m.metodo_pagamento, m.bandeira, m.parcelas ? `${m.parcelas}x` : null].filter(Boolean).join(' · ') || '—'}
                                  </small>
                                </td>
                                <td className="text-end"><small>{brl(m.valor_bruto)}</small></td>
                                <td className="text-end"><small>{brl(m.tarifa)}</small></td>
                                <td className="text-end"><small><strong>{brl(m.valor_liquido)}</strong></small></td>
                                <td>
                                  <span className={`badge ${m.status === 'available' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                    {m.status === 'available' ? 'Disponível' : 'Pendente'}
                                  </span>
                                </td>
                                <td><small>{data(m.disponivel_em)}</small></td>
                                <td>
                                  <small>
                                    {m.repasse_id ? `${m.repasse_id} · ${data(m.repasse_chegada)}` : '—'}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setMovimentos(null)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setMovimentos(null)}></div>
        </>
      )}
    </div>
  );
};

export default StripeEventos;
